import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../services/userService';
import { toast } from 'react-toastify';
import socketService from '../../services/socketService';

interface NavbarProps {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Navbar: React.FC<NavbarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userData, setUserData] = useState<{
    name: string;
    email?: string;
    phoneNumber?: string;
    subscription?: {
      plan?: string;
      expiresAt?: string;
    };
    credit?: number;
  } | null>(null);
  const profileMenuRef = useRef<HTMLLIElement>(null);
  // Always use light mode
  const isDarkMode = false;

  // Load user data from API
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      const fetchUserData = async () => {
        try {
          // Fetch user data from API
          const response = await getCurrentUser();
          if (response && response.data) {
            setUserData({
              name: response.data.name || 'کاربر رازی',
              email: response.data.email,
              phoneNumber: response.data.mobile,
              subscription: response.data.subscription,
              credit: response.data.credit
            });
            
            // Also store in localStorage for other components that might need it
            localStorage.setItem('userData', JSON.stringify({
              name: response.data.name || 'کاربر رازی',
              email: response.data.email,
              phoneNumber: response.data.mobile,
              subscription: response.data.subscription,
              credit: response.data.credit
            }));
          }
        } catch (error) {

          toast.error('خطا در دریافت اطلاعات کاربر', {
            position: 'top-center',
            rtl: true
          });
          
          // Fallback to localStorage if API fails
          const userDataFromStorage = localStorage.getItem('userData');
          if (userDataFromStorage) {
            try {
              setUserData(JSON.parse(userDataFromStorage));
            } catch (error) {

            }
          }
        }
      };
      
      fetchUserData();
    }
  }, []);

  // Handle clicking outside profile menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle logout
  const handleLogout = () => {
    // Check if user is admin
    const userDataStr = localStorage.getItem('userData');
    const userRole = localStorage.getItem('userRole');
    let isAdmin = userRole === 'admin';
    
    if (!isAdmin && userDataStr) {
      try {
        const parsedUserData = JSON.parse(userDataStr);
        isAdmin = parsedUserData.userType === 'Admin';
      } catch (error) {
        // Ignore parse error
      }
    }
    
    // Clear authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userRole');
    
    // Disconnect socket
    socketService.disconnect();
    
    setShowProfileMenu(false);
    
    // Redirect based on user type
    if (isAdmin) {
      navigate('/admin');
    } else {
      navigate('/login');
    }
  };

  // Ensure light mode is always applied
  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.classList.remove('dark');
    
    // Save light mode preference to localStorage
    localStorage.setItem('darkMode', 'false');
  }, []);

  // Dark mode has been removed, app is always in light mode

  return (
    <nav className={`fixed top-0 left-0 right-0 border-b border-slate-200 flex justify-between items-center px-4 py-3 z-90 bg-white transition-all duration-300 ${sidebarOpen ? 'md:mr-60' : 'mr-0'}`}>
      <div className="navbar-links flex gap-2 items-center">
        {/* Mobile menu toggle */}
        <button 
          className="md:hidden text-slate-500 hover:text-slate-700" 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          {sidebarOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          )}
        </button>
     
        <Link to="/faqs" target='_blank' className="py-2 px-4 text-sm bg-slate-50 rounded-sm text-slate-800 border-1 border-slate-200 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 512 512" fill="currentColor">
            <path d="M480 256A224 224 0 1 0 32 256a224 224 0 1 0 448 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm168.7-86.2c6.5-24.6 28.7-41.8 54.2-41.8l57.1 0c35.5 0 64 29 64 64.3c0 24-13.4 46.2-34.9 57.2L272 268.3l0 19.7c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-29.5c0-6 3.4-11.5 8.7-14.3l45.8-23.4c10.7-5.4 17.5-16.6 17.5-28.7c0-17.8-14.4-32.3-32-32.3l-57.1 0c-10.9 0-20.5 7.4-23.2 17.9l-.2 .7c-2.2 8.5-11 13.7-19.5 11.4s-13.7-11-11.4-19.5l.2-.7zM232 352a24 24 0 1 1 48 0 24 24 0 1 1 -48 0z"></path>
          </svg>
          <span>سوالات متداول</span>
        </Link>
      </div>

      <div className="flex items-center gap-4">
        <ul className="navbar-nav flex items-center gap-4">
          {/* Theme Toggle removed - app is now light mode only */}

          <li className="nav-item relative" ref={profileMenuRef}>
            <div 
              className="avatar cursor-pointer" 
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                <svg width="20px" height="20px" viewBox="0 0 24 24" fill="currentColor"><path d="M15.71,12.71a6,6,0,1,0-7.42,0,10,10,0,0,0-6.22,8.18,1,1,0,0,0,2,.22,8,8,0,0,1,15.9,0,1,1,0,0,0,1,.89h.11a1,1,0,0,0,.88-1.1A10,10,0,0,0,15.71,12.71ZM12,12a4,4,0,1,1,4-4A4,4,0,0,1,12,12Z"></path></svg>
              </div>
            </div>
            
            {/* Profile Dropdown Menu */}
            {showProfileMenu && (
              <div className="absolute left-0 z-10 mt-2 w-48 origin-top-left rounded-md bg-white focus:outline-none border border-slate-200">
                {userData && (
                  <div className="px-4 py-3 border-b border-slate-200">
                    <p className="text-sm font-medium text-slate-800">{userData.name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {userData.email || userData.phoneNumber || ''}
                    </p>
                  </div>
                )}
                <Link 
                  to="/profile" 
                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                  onClick={() => setShowProfileMenu(false)}
                >
                  اطلاعات هویتی
                </Link>
                 <Link 
                  to="/profile?tab=security" 
                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                  onClick={() => setShowProfileMenu(false)}
                >
                  تنظیمات امنیتی
                </Link>
                <button
                  className="block w-full text-right px-4 py-2 text-sm text-red-600 hover:bg-slate-100"
                  onClick={handleLogout}
                >
                  خروج
                </button>
              </div>
            )}
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
